import { useState } from 'react'
import { Plus, X } from 'lucide-react'
import type { RequirementAcceptanceCriterion } from '@ispot/shared'
import type { SectionEditorProps } from './types'

type Value = RequirementAcceptanceCriterion[] | undefined

interface Props extends SectionEditorProps<Value> {}

/**
 * Acceptance criteria checklist. Existing detail panel had this — re-rendered
 * here within the Granola-style editor frame. Same data shape, same toggle
 * semantics; the schema keeps both `done` and `met` for compat.
 */
export function CriteriaSection({ value, onChange }: Props) {
  const items = value ?? []
  const [draft, setDraft] = useState('')

  function toggle(i: number) {
    const next = items.map((c, idx) =>
      idx === i ? { ...c, done: !(c.done ?? c.met ?? false), met: !(c.done ?? c.met ?? false) } : c,
    )
    onChange(next)
  }

  function remove(i: number) {
    onChange(items.filter((_, idx) => idx !== i))
  }

  function add() {
    const text = draft.trim()
    if (!text) return
    onChange([...items, { text, done: false, met: false }])
    setDraft('')
  }

  return (
    <ul className="flex flex-col" style={{ gap: 2, listStyle: 'none', padding: 0, margin: 0 }}>
      {items.map((ac, i) => {
        const met = ac.done ?? ac.met ?? false
        return (
          <li
            key={i}
            className="group/ac flex items-start gap-2 px-1 py-1 rounded"
            style={{ transition: 'background 120ms' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-warm-100)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <button
              type="button"
              onClick={() => toggle(i)}
              aria-label={met ? 'Mark incomplete' : 'Mark complete'}
              className="flex items-center justify-center cursor-pointer mt-1 border-0"
              style={{
                width: 13, height: 13, flexShrink: 0,
                borderRadius: 3,
                border: `1.5px solid ${met ? 'var(--color-success)' : 'var(--color-warm-400)'}`,
                background: met ? 'var(--color-success)' : 'transparent',
              }}
            >
              {met && (
                <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                  <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
            <span
              className="flex-1"
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: met ? 'var(--color-fg-muted)' : 'var(--color-fg)',
                textDecoration: met ? 'line-through' : undefined,
              }}
            >
              {ac.text}
            </span>
            <button
              type="button"
              onClick={() => remove(i)}
              aria-label="Remove criterion"
              className="opacity-0 group-hover/ac:opacity-100 cursor-pointer bg-transparent border-0 mt-1"
              style={{ color: 'var(--color-warm-400)', transition: 'opacity 120ms' }}
            >
              <X size={11} />
            </button>
          </li>
        )
      })}
      <li className="flex items-center gap-2 px-1 py-1">
        <Plus size={13} style={{ color: 'var(--color-warm-400)', flexShrink: 0 }} />
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') add() }}
          onBlur={() => { if (draft.trim()) add() }}
          placeholder="Add criterion…"
          className="flex-1 bg-transparent outline-none border-0"
          style={{
            fontSize: 14,
            color: 'var(--color-fg)',
            fontFamily: 'var(--font-sans)',
          }}
        />
      </li>
    </ul>
  )
}
